import { useState, useEffect, useMemo, useRef } from "react";
import { Input, message } from "antd";
import type { InputRef } from "antd";
import axios from "../../../../common/api/axios";
import { formatSlotDisplay } from "../../../../common/utils/slot";
import {
  EXPIRY_DATE_ERROR,
  getMinExpiryDate,
  isExpiryDateAllowed,
} from "../../../../common/utils/expiryDate";
import { resolveInwardQuantityPrefill } from "../../../../common/utils/inwardQuantityPrefill";
import {
  InwardBinLocationSubmission,
  PendingInwardOrder,
} from "../../types";
import * as Styles from "./styles";

interface SlotPosition {
  x: number;
  y: number;
  z: number;
}

interface InwardModalProps {
  gate: {
    slot: SlotPosition;
    kind?: "dynamic" | "static";
  };
  onSubmit: (submission: InwardBinLocationSubmission) => void;
  submitting: boolean;
  errorMessage?: string | null;
  pendingOrders: PendingInwardOrder[];
  pendingOrdersLoading?: boolean;
  pendingOrdersError?: string | null;
  initialSkuId?: string;
}

function formatOrderLabel(order: PendingInwardOrder): string {
  return order.inwardOrderReferenceId || "-";
}

interface OrderSkuRow {
  item: any;
  itemIndex: number;
  sku: any;
  skuIndex: number;
}

function getOrderSkuRows(order: PendingInwardOrder | null): OrderSkuRow[] {
  const items = Array.isArray((order as any)?.inwardOrderItems)
    ? (order as any).inwardOrderItems
    : (order as any)?.inwardOrderItem
      ? [(order as any).inwardOrderItem]
      : [];

  return items.flatMap((item: any, itemIndex: number): OrderSkuRow[] =>
    (item?.inwardOrderSkus ?? []).map((sku: any, skuIndex: number): OrderSkuRow => ({
      item,
      itemIndex,
      sku,
      skuIndex,
    }))
  );
}

export default function InwardModal({
  gate,
  onSubmit,
  submitting,
  errorMessage,
  pendingOrders,
  pendingOrdersLoading = false,
  pendingOrdersError,
  initialSkuId = "",
}: InwardModalProps) {
  const [selectedOrderId, setSelectedOrderId] = useState("");
  const [toteId, setToteId] = useState("");
  const [caseId, setCaseId] = useState("");
  const [skuId, setSkuId] = useState(initialSkuId);
  const [quantity, setQuantity] = useState("");
  const [expirationDate, setExpirationDate] = useState("");
  const [validationError, setValidationError] = useState<string | null>(null);
  const [staticSlotBinId, setStaticSlotBinId] = useState("");
  const [staticSlotLoading, setStaticSlotLoading] = useState(false);
  const [staticSlotError, setStaticSlotError] = useState<string | null>(null);
  const [creatingRemainderOrder, setCreatingRemainderOrder] = useState(false);
  const [remainderOrderCreated, setRemainderOrderCreated] = useState(false);
  const [remainderOrderError, setRemainderOrderError] = useState<string | null>(
    null
  );
  const [animationStarted, setAnimationStarted] = useState(false);
  const firstInputRef = useRef<InputRef>(null);
  // Set once the operator types in the quantity field, so the pre-fill stops tracking the
  // order and never overwrites what they entered.
  const quantityEditedRef = useRef(false);

  const slotLabel = formatSlotDisplay(gate.slot);
  const isStaticGate = gate.kind === "static";
  const resolvedToteId = isStaticGate ? staticSlotBinId.trim() : toteId.trim();
  const hasPlacementIdentifier = resolvedToteId !== "";

  // "Today" is fixed for the life of the modal — it is open for seconds, not across midnight.
  const minExpiryDate = useMemo(() => getMinExpiryDate(new Date()), []);
  const isExpiryDateValid = isExpiryDateAllowed(expirationDate, minExpiryDate);

  const isFormValid =
    selectedOrderId.trim() !== "" &&
    hasPlacementIdentifier &&
    skuId.trim() !== "" &&
    quantity.trim() !== "" &&
    Number(quantity) > 0 &&
    isExpiryDateValid &&
    (!isStaticGate || caseId.trim() !== "") &&
    (!isStaticGate || (!staticSlotLoading && !staticSlotError));

  useEffect(() => {
    const timer = setTimeout(() => {
      firstInputRef.current?.focus();
    }, 300);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    setSkuId(initialSkuId);
    setSelectedOrderId("");
    setQuantity("");
    setExpirationDate("");
    setValidationError(null);
  }, [initialSkuId]);

  useEffect(() => {
    if (!isStaticGate) {
      setStaticSlotBinId("");
      setStaticSlotError(null);
      setStaticSlotLoading(false);
      return;
    }

    let cancelled = false;
    setStaticSlotLoading(true);
    setStaticSlotError(null);
    setStaticSlotBinId("");

    axios
      .get(`Slot/slot/${gate.slot.x}/${gate.slot.y}/${gate.slot.z}`, {
        headers: { Accept: "application/json", tenantId: 1 },
      })
      .then((res) => {
        if (cancelled) return;

        const binId =
          typeof res.data?.binId === "string" ? res.data.binId.trim() : "";
        if (!binId) {
          setStaticSlotError("No tote ID found for this static slot.");
          return;
        }

        setStaticSlotBinId(binId);
      })
      .catch((error: any) => {
        if (cancelled) return;

        const serverMessage =
          error?.response?.data?.toString?.() ||
          error?.response?.data?.title ||
          "Unable to load the static slot tote ID.";
        setStaticSlotError(serverMessage);
      })
      .finally(() => {
        if (!cancelled) {
          setStaticSlotLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [gate.slot.x, gate.slot.y, gate.slot.z, isStaticGate]);

  useEffect(() => {
    const timer = setTimeout(() => setAnimationStarted(true), 200);
    return () => clearTimeout(timer);
  }, []);

  const selectedOrder = useMemo(
    () =>
      pendingOrders.find(
        (order) => order.inwardOrderReferenceId === selectedOrderId
      ) ?? null,
    [pendingOrders, selectedOrderId]
  );

  const selectedOrderSkuRows = useMemo(
    () => getOrderSkuRows(selectedOrder),
    [selectedOrder]
  );

  const matchingPendingOrders = useMemo(() => {
    const trimmedSkuId = skuId.trim();
    if (!trimmedSkuId) {
      return [];
    }

    return pendingOrders.filter((order) =>
      getOrderSkuRows(order).some(
        ({ sku }) => sku.skuId?.trim() === trimmedSkuId
      )
    );
  }, [pendingOrders, skuId]);

  useEffect(() => {
    if (!hasPlacementIdentifier || !skuId.trim()) {
      setSelectedOrderId("");
      return;
    }

    if (matchingPendingOrders.length === 0) {
      setSelectedOrderId("");
      return;
    }

    if (
      matchingPendingOrders.some(
        (order) => order.inwardOrderReferenceId === selectedOrderId
      )
    ) {
      return;
    }

    const randomIndex = Math.floor(
      Math.random() * matchingPendingOrders.length
    );
    setSelectedOrderId(
      matchingPendingOrders[randomIndex].inwardOrderReferenceId
    );
  }, [hasPlacementIdentifier, matchingPendingOrders, selectedOrderId, skuId]);

  const selectedMatchingSkuRows = useMemo(
    () =>
      selectedOrderSkuRows.filter(
        ({ sku }) => sku.skuId?.trim() === skuId.trim()
      ),
    [selectedOrderSkuRows, skuId]
  );

  const selectedBatchText = useMemo(() => {
    const batchIds = Array.from(
      new Set(
        selectedMatchingSkuRows
          .map(({ sku }) => sku.batchId?.trim())
          .filter((batchId): batchId is string => Boolean(batchId))
      )
    );

    return batchIds.length > 0 ? batchIds.join(", ") : "-";
  }, [selectedMatchingSkuRows]);

  const displayQuantityRows = useMemo(() => {
    if (selectedMatchingSkuRows.length > 0) {
      return selectedMatchingSkuRows;
    }

    const firstMatchingOrder = matchingPendingOrders[0] ?? null;
    return getOrderSkuRows(firstMatchingOrder).filter(
      ({ sku }) => sku.skuId?.trim() === skuId.trim()
    );
  }, [matchingPendingOrders, selectedMatchingSkuRows, skuId]);

  const inwardOrderQuantities = useMemo(
    () => displayQuantityRows.map(({ sku }) => sku.quantity),
    [displayQuantityRows]
  );

  const inwardOrderQuantityText = useMemo(() => {
    const quantities = inwardOrderQuantities
      .filter((value): value is number => value !== undefined && value !== null)
      .map((value) => String(value));

    return quantities.length > 0 ? quantities.join(", ") : "";
  }, [inwardOrderQuantities]);

  const quantityPrefill = useMemo(
    () => resolveInwardQuantityPrefill(inwardOrderQuantities),
    [inwardOrderQuantities]
  );

  // A new SKU is a new question, so the field goes back to tracking the order.
  useEffect(() => {
    quantityEditedRef.current = false;
  }, [skuId]);

  // Keep the field on the order's quantity until the operator takes it over. It has to keep
  // tracking rather than fill once: the matching order is only resolved after the tote and
  // SKU are both in, and it can change again as the pending-order poll comes back.
  useEffect(() => {
    if (quantityEditedRef.current) {
      return;
    }

    setQuantity(quantityPrefill);
  }, [quantityPrefill]);

  const maxInwardOrderQuantity = useMemo(() => {
    const quantities = selectedMatchingSkuRows
      .map(({ sku }) => sku.quantity)
      .filter((value): value is number => value !== undefined && value !== null);

    return quantities.length > 0 ? Math.max(...quantities) : null;
  }, [selectedMatchingSkuRows]);

  const targetSkuRow = useMemo(() => {
    if (selectedMatchingSkuRows.length === 0) {
      return null;
    }

    if (maxInwardOrderQuantity === null) {
      return selectedMatchingSkuRows[0];
    }

    return (
      selectedMatchingSkuRows.find(
        ({ sku }) => sku.quantity === maxInwardOrderQuantity
      ) ?? selectedMatchingSkuRows[0]
    );
  }, [maxInwardOrderQuantity, selectedMatchingSkuRows]);

  const targetOrderQuantity = targetSkuRow?.sku.quantity ?? null;
  const scannedQuantity = Number(quantity);
  const remainderQuantity =
    !isStaticGate &&
    targetOrderQuantity !== null &&
    quantity.trim() !== "" &&
    scannedQuantity > 0 &&
    scannedQuantity < targetOrderQuantity
      ? targetOrderQuantity - scannedQuantity
      : 0;

  const shouldShowRemainderOrderAction =
    !isStaticGate &&
    selectedOrder !== null &&
    targetSkuRow !== null &&
    remainderQuantity > 0;

  const selectedOrderText = selectedOrder
    ? formatOrderLabel(selectedOrder)
    : "-";

  const hasNoPendingOrders =
    !pendingOrdersError &&
    !pendingOrdersLoading &&
    pendingOrders.length === 0;

  const clearValidation = () => {
    if (validationError) {
      setValidationError(null);
    }
  };

  useEffect(() => {
    setRemainderOrderCreated(false);
    setRemainderOrderError(null);
  }, [quantity, selectedOrderId, skuId]);

  const handleCreateRemainderOrder = async () => {
    if (
      !selectedOrder ||
      !targetSkuRow ||
      remainderQuantity <= 0 ||
      creatingRemainderOrder
    ) {
      return;
    }

    setCreatingRemainderOrder(true);
    setRemainderOrderError(null);

    const inwardOrderSku: {
      skuId: string;
      quantity: number;
      partition: string;
      batchId?: string;
    } = {
      skuId: skuId.trim(),
      quantity: remainderQuantity,
      partition: "0",
    };

    const batchId = targetSkuRow.sku.batchId?.trim();
    if (batchId) {
      inwardOrderSku.batchId = batchId;
    }

    const payload: {
      inwardOrderItem: {
        inwardOrderSkus: Array<typeof inwardOrderSku>;
      };
      orchestrationId?: string;
    } = {
      inwardOrderItem: {
        inwardOrderSkus: [inwardOrderSku],
      },
    };

    if (selectedOrder.orchestrationId) {
      payload.orchestrationId = selectedOrder.orchestrationId;
    }

    try {
      await axios.post("InwardOrder", payload, {
        headers: { Accept: "application/json", tenantId: 1 },
      });
      setRemainderOrderCreated(true);
      message.success("Inward order created for the remaining quantity.");
    } catch (error: any) {
      const serverMessage =
        error?.response?.data?.toString?.() ||
        error?.response?.data?.title ||
        error?.message ||
        "Failed to create inward order.";
      setRemainderOrderError(serverMessage);
    } finally {
      setCreatingRemainderOrder(false);
    }
  };

  const handleDone = () => {
    if (!isFormValid || submitting) {
      return;
    }

    if (!selectedOrder) {
      setValidationError("Select a pending inward order.");
      return;
    }

    const scannedToteId = toteId.trim();
    const scannedCaseId = caseId.trim();
    const scannedSkuId = skuId.trim();
    const scannedQuantity = Number(quantity);

    if (isStaticGate && staticSlotLoading) {
      setValidationError("Static slot tote ID is still loading.");
      return;
    }

    if (isStaticGate && staticSlotError) {
      setValidationError(staticSlotError);
      return;
    }

    if (!resolvedToteId) {
      setValidationError("Tote ID is required.");
      return;
    }

    if (isStaticGate && !scannedCaseId) {
      setValidationError("Case ID is required.");
      return;
    }

    if (selectedMatchingSkuRows.length === 0) {
      setValidationError("No pending inward order found for this SKU ID.");
      return;
    }

    if (
      maxInwardOrderQuantity !== null &&
      scannedQuantity > maxInwardOrderQuantity
    ) {
      setValidationError(
        "Quantity cannot be greater than the inward order quantity."
      );
      return;
    }

    const enteredExpirationDate = expirationDate.trim();

    // The input's `min` greys out earlier dates in the native picker, but a typed or pasted
    // value still reaches here.
    if (!isExpiryDateAllowed(enteredExpirationDate, minExpiryDate)) {
      setValidationError(EXPIRY_DATE_ERROR);
      return;
    }

    setValidationError(null);
    onSubmit({
      inwardOrderReferenceId: selectedOrder.inwardOrderReferenceId,
      skuId: scannedSkuId,
      caseId: isStaticGate ? scannedCaseId : "",
      toteId: isStaticGate ? resolvedToteId : scannedToteId,
      inwardedQuantity: scannedQuantity,
      ...(enteredExpirationDate
        ? { expirationDate: enteredExpirationDate }
        : {}),
    });
  };

  return (
    <Styles.ModalContainer>
      <Styles.Column>
        <Styles.ColumnTitle>Inward Order</Styles.ColumnTitle>

        {isStaticGate ? (
          <Styles.FormGroup>
            <Styles.FormLabel>Case ID</Styles.FormLabel>
            <Input
              ref={firstInputRef}
              placeholder="Scan or enter Case ID"
              value={caseId}
              onChange={(e) => {
                setCaseId(e.target.value);
                setValidationError(null);
              }}
              size="large"
            />
            {staticSlotLoading ? (
              <Styles.FieldInfo>Loading static slot tote ID...</Styles.FieldInfo>
            ) : null}
            {staticSlotBinId ? (
              <Styles.FieldInfo>Tote ID: {staticSlotBinId}</Styles.FieldInfo>
            ) : null}
            {staticSlotError ? (
              <Styles.FieldError>{staticSlotError}</Styles.FieldError>
            ) : null}
            {pendingOrdersError && (
              <Styles.FieldError>{pendingOrdersError}</Styles.FieldError>
            )}
            {hasNoPendingOrders ? (
              <Styles.FieldError>
                No pending inward orders found.
              </Styles.FieldError>
            ) : null}
          </Styles.FormGroup>
        ) : (
          <Styles.FormGroup>
            <Styles.FormLabel>Tote ID</Styles.FormLabel>
            <Input
              ref={firstInputRef}
              placeholder="Scan or enter Tote ID"
              value={toteId}
              onChange={(e) => {
                setToteId(e.target.value);
                setValidationError(null);
              }}
              size="large"
            />
            {pendingOrdersError && (
              <Styles.FieldError>{pendingOrdersError}</Styles.FieldError>
            )}
            {hasNoPendingOrders ? (
              <Styles.FieldError>
                No pending inward orders found.
              </Styles.FieldError>
            ) : null}
          </Styles.FormGroup>
        )}

        <Styles.FormGroup>
          <Styles.FormLabel>SKU ID</Styles.FormLabel>
          <Input
            placeholder="Scan or enter SKU ID"
            value={skuId}
            onChange={(e) => {
              setSkuId(e.target.value);
              setQuantity("");
              setSelectedOrderId("");
              setValidationError(null);
            }}
            size="large"
          />
          {!pendingOrdersError &&
          hasPlacementIdentifier &&
          skuId.trim() !== "" &&
          matchingPendingOrders.length === 0 ? (
            <Styles.FieldError>
              No pending inward order found for this SKU ID.
            </Styles.FieldError>
          ) : null}
        </Styles.FormGroup>

        <Styles.FormGroup>
          <Styles.FormLabel>Quantity</Styles.FormLabel>
          <Input
            placeholder="Enter quantity"
            value={quantity}
            onChange={(e) => {
              const value = e.target.value;
              if (value === "" || /^\d+$/.test(value)) {
                quantityEditedRef.current = true;
                setQuantity(value);
              }
              clearValidation();
            }}
            size="large"
          />
          {inwardOrderQuantityText && (
            <Styles.FieldInfo>
              Inward Order Quantity: {inwardOrderQuantityText}
            </Styles.FieldInfo>
          )}
        </Styles.FormGroup>

        <Styles.FormGroup>
          <Styles.FormLabel>Expiry Date (optional)</Styles.FormLabel>
          <Input
            type="date"
            value={expirationDate}
            min={minExpiryDate}
            onChange={(e) => {
              setExpirationDate(e.target.value);
              clearValidation();
            }}
            size="large"
          />
          {isExpiryDateValid ? (
            <Styles.FieldInfo>
              Leave blank if this SKU is not expiry-tracked.
            </Styles.FieldInfo>
          ) : (
            <Styles.FieldError>{EXPIRY_DATE_ERROR}</Styles.FieldError>
          )}
        </Styles.FormGroup>

        {shouldShowRemainderOrderAction && (
          <Styles.RemainderOrderPanel>
            <Styles.RemainderText>
              Click below to create a new inward order for the remaining{" "}
              {remainderQuantity} quantity.
            </Styles.RemainderText>
            {remainderOrderError && (
              <Styles.FieldError>{remainderOrderError}</Styles.FieldError>
            )}
            {remainderOrderCreated && (
              <Styles.SuccessText>
                Inward order created for remaining quantity.
              </Styles.SuccessText>
            )}
            <Styles.CreateOrderButton
              disabled={remainderOrderCreated || creatingRemainderOrder}
              loading={creatingRemainderOrder}
              onClick={handleCreateRemainderOrder}
            >
              Create Inward Order
            </Styles.CreateOrderButton>
          </Styles.RemainderOrderPanel>
        )}
      </Styles.Column>

      <Styles.Column>
        <Styles.ColumnTitle>Tote Configuration</Styles.ColumnTitle>
        <Styles.ToteVisual>
          <Styles.ToteInner />
        </Styles.ToteVisual>
        <Styles.SlotLabel>Slot: {slotLabel}.</Styles.SlotLabel>
        <Styles.BatchLabel>Order ID: {selectedOrderText}</Styles.BatchLabel>
        <Styles.BatchLabel>Batch: {selectedBatchText}</Styles.BatchLabel>
      </Styles.Column>

      <Styles.Column>
        <Styles.ColumnTitle>Confirm Placement</Styles.ColumnTitle>
        <Styles.PlacementContainer>
          <Styles.SlotVisual>
            <Styles.ToteDropElement
              className={animationStarted ? "animate" : ""}
            >
              TOTE
            </Styles.ToteDropElement>
            <Styles.HighlightZone />
          </Styles.SlotVisual>

          <Styles.PlacementText>
            Place tote into the highlighted slot.
          </Styles.PlacementText>

          {validationError && (
            <Styles.ErrorText>{validationError}</Styles.ErrorText>
          )}

          {errorMessage && (
            <Styles.ErrorText>{errorMessage}</Styles.ErrorText>
          )}

          <Styles.DoneButton
            disabled={!isFormValid || submitting}
            loading={submitting}
            onClick={handleDone}
          >
            Done
          </Styles.DoneButton>

          <Styles.WarningText>
            Acknowledge only after ensuring the tote is placed.
          </Styles.WarningText>
        </Styles.PlacementContainer>
      </Styles.Column>
    </Styles.ModalContainer>
  );
}
